import { use } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';

import CareConnectSettingsProviderContext from 'features/care-connect-settings/contexts/care-connect-settings-provider-context';
import { ReadCallPlanVcareNumber } from 'features/care-connect-settings/domain/models/read-call-plan-vcare-number';
import useCallPlanExternalNumbers from 'features/care-connect-settings/hooks/call-plan-external-numbers';
import useExternalNumberValidation from 'features/care-connect-settings/hooks/external-number-validation';
import styles from 'features/care-connect-settings/views/components/call-plan-external-numbers/call-plan-external-numbers.module.scss';

const CallPlanExternalNumbers = (): React.JSX.Element => {
  const { t } = useTranslation('careConnectSettings', { keyPrefix: 'callPlanExternalNumbers' });
  const { callPlanDetails } = use(CareConnectSettingsProviderContext);
  const { callPlanForm, isEditing, readCallPlanData } = callPlanDetails;
  const {
    externalNumbers,
    externalNumbersEmptyErrorMessage,
    canAddExternalNumber,
    addExternalNumber,
    removeExternalNumber,
    isUsedForOutGoingCalls,
    selectForOutGoingCalls,
  } = useCallPlanExternalNumbers(callPlanForm);
  const { getValidationRules, handleDigitsOnlyChange, handleBeforeInput } = useExternalNumberValidation();

  const renderExternalNumberRow = (externalNumber: (typeof externalNumbers)[number], index: number): React.JSX.Element => {
    const isSelectedForOutGoingCalls = isUsedForOutGoingCalls(index);

    return (
      <div key={externalNumber.id} className={styles.externalNumberInput}>
        <Controller
          name={`externalNumbers.${index}.number`}
          control={callPlanForm.control}
          rules={getValidationRules(index, externalNumber.number)}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              className={styles.externalNumberTextField}
              error={!!error}
              helperText={error?.message}
              value={field.value ?? ''}
              slotProps={{
                htmlInput: {
                  maxLength: 15,
                  inputMode: 'numeric',
                  onBeforeInput: handleBeforeInput,
                },
              }}
              onChange={(event) => handleDigitsOnlyChange(event.target.value, field.onChange)}
              onBlur={field.onBlur}
            />
          )}
        />
        <Radio
          checked={isSelectedForOutGoingCalls}
          className={styles.outgoingCallsRadioButton}
          onChange={(_, checked) => checked && selectForOutGoingCalls(index)}
        />
        <IconButton className={styles.deleteButton} onClick={() => removeExternalNumber(index)}>
          <DeleteForeverOutlinedIcon />
        </IconButton>
      </div>
    );
  };

  const renderViewingExternalNumberRow = (externalNumber: ReadCallPlanVcareNumber): React.JSX.Element => (
    <div key={externalNumber.number} className={styles.viewingExternalNumberRow}>
      <Typography variant="subtitle1">{externalNumber.number}</Typography>
      {externalNumber.isUsedForOutGoingCalls && (
        <Radio checked={externalNumber.isUsedForOutGoingCalls} disableRipple className={styles.viewingOutgoingCallsRadioButton} />
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <Typography variant="h5">{t('title')}</Typography>
      <Typography variant="subtitle1">{t('subtitle')}</Typography>
      {isEditing
        ? externalNumbers.map((externalNumber, index) => renderExternalNumberRow(externalNumber, index))
        : readCallPlanData?.externalNumbers.map(renderViewingExternalNumberRow)}
      {externalNumbersEmptyErrorMessage && (
        <Typography color="error" variant="body2">
          {externalNumbersEmptyErrorMessage}
        </Typography>
      )}
      {isEditing && canAddExternalNumber && (
        <Button variant="text" type="button" className={styles.addExternalNumberButton} onClick={addExternalNumber}>
          <AddCircleOutlineIcon sx={{ marginRight: 1 }} fontSize="small" />
          <Typography variant="body1" color="primary">
            {t('addExternalNumberButton')}
          </Typography>
        </Button>
      )}
    </div>
  );
};

export default CallPlanExternalNumbers;
